import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Trash2, Maximize2, Minimize2 } from 'lucide-react';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { useTheme } from '../../contexts/ThemeContext';
import { useModule } from '../../contexts/ModuleContext';
import { hasValidAuth, getItem, AUTH_KEYS, CHAT_SESSION_KEY_PREFIX } from '../../utils/authStorage';
import { fetchWithAuthRetry } from '../../utils/helpers';
import { ENDPOINTS } from '../../utils/appConfig';
import asviAssistantIcon from '../../assets/chatbot/asvi-assistant.jpg';
import './chatbot.css';

const createSessionId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);

// Sessions belong to a user server-side; a key shared across logins would send another user's session id.
const sessionStorageKey = (projectKey) =>
  `${CHAT_SESSION_KEY_PREFIX}${getItem(AUTH_KEYS.USERNAME) || 'anonymous'}_${projectKey}`;

const getOrCreateSessionId = (projectKey) => {
  if (!projectKey) return null;
  const key = sessionStorageKey(projectKey);
  let id = localStorage.getItem(key);
  if (!id) {
    id = createSessionId();
    localStorage.setItem(key, id);
  }
  return id;
};

const welcomeForProject = (projectName) =>
  projectName
    ? i18n.t('chatbot.welcomeProject', { project: projectName })
    : i18n.t('chatbot.welcomeNoProject');

const Chatbot = ({ title, displayMode = 'widget' }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { activeProject } = useModule();
  const [isOpen, setIsOpen] = useState(displayMode === 'embedded');
  const [isMaximized, setIsMaximized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const projectKey = useMemo(() => {
    if (!activeProject) return null;
    return activeProject.numericId ?? activeProject.code ?? activeProject.id ?? null;
  }, [activeProject]);

  const projectName = activeProject?.name || activeProject?.displayName || null;
  const chatApiUrl = projectKey != null ? ENDPOINTS.CHAT(projectKey) : null;
  const hasProject = Boolean(projectKey && chatApiUrl);

  const [sessionId, setSessionId] = useState(() => getOrCreateSessionId(projectKey));
  const [messages, setMessages] = useState(() => [
    {
      id: 1,
      sender: 'bot',
      text: welcomeForProject(projectName),
      timestamp: new Date().toISOString(),
    },
  ]);

  const messagesEndRef = useRef(null);
  const prevProjectKeyRef = useRef(projectKey);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Switch session when active project changes (PRD-09)
  useEffect(() => {
    const prev = prevProjectKeyRef.current;
    prevProjectKeyRef.current = projectKey;

    if (projectKey == null) {
      setSessionId(null);
      setMessages([
        {
          id: Date.now(),
          sender: 'bot',
          text: welcomeForProject(null),
          timestamp: new Date().toISOString(),
        },
      ]);
      return;
    }

    const nextSession = getOrCreateSessionId(projectKey);
    setSessionId(nextSession);

    if (prev !== projectKey) {
      setMessages([
        {
          id: Date.now(),
          sender: 'bot',
          text: welcomeForProject(projectName),
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }, [projectKey, projectName]);

  // Fetch session history when project + session are ready
  useEffect(() => {
    if (!hasValidAuth() || !hasProject || !sessionId || !chatApiUrl) return;

    let cancelled = false;

    const fetchHistory = async () => {
      try {
        const token = getItem(AUTH_KEYS.ACCESS_TOKEN);
        const historyUrl = `${chatApiUrl}/history/${sessionId}`;
        const response = await fetchWithAuthRetry(historyUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0 && !cancelled) {
          const formatted = data.map((msg, index) => ({
            id: msg.id || Date.now() + index,
            sender: msg.role === 'user' ? 'user' : 'bot',
            text: msg.content,
            timestamp: msg.createdAt || new Date().toISOString(),
          }));
          setMessages(formatted);
        }
      } catch (err) {
        console.warn('Failed to fetch chat history from backend:', err);
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [sessionId, chatApiUrl, hasProject]);

  if (!hasValidAuth()) {
    return null;
  }

  const handleSendMessage = async (text) => {
    if (!hasProject || !chatApiUrl || !sessionId) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          sender: 'bot',
          text: 'Select a **construction project** first, then ask your question.',
          timestamp: new Date().toISOString(),
        },
      ]);
      return;
    }

    const userMsg = {
      id: Date.now(),
      sender: 'user',
      text: text,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const token = getItem(AUTH_KEYS.ACCESS_TOKEN);
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const send = (sid) =>
        fetchWithAuthRetry(chatApiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ query: text, sessionId: sid }),
        });

      let response = await send(sessionId);
      if (response.status === 404) {
        // Stored session is unknown or owned by someone else — continue in a fresh one.
        const freshId = createSessionId();
        localStorage.setItem(sessionStorageKey(projectKey), freshId);
        setSessionId(freshId);
        response = await send(freshId);
      }

      if (!response.ok) {
        throw new Error(`Server returned HTTP ${response.status}`);
      }

      const data = await response.json();
      const botMsg = {
        id: Date.now() + 1,
        sender: 'bot',
        text: data.answer || data.message || 'No answer received.',
        source: data.source || null,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, botMsg]);
    } catch (err) {
      console.warn('Chatbot API request error:', err);
      const errorMsg = {
        id: Date.now() + 1,
        sender: 'bot',
        text: `**Unable to reach assistant** (\`${chatApiUrl}\`).\n\n*Error:* ${err.message}`,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    if (!projectKey) return;
    const newId = createSessionId();
    localStorage.setItem(sessionStorageKey(projectKey), newId);
    setSessionId(newId);
    setMessages([
      {
        id: Date.now(),
        sender: 'bot',
        text: welcomeForProject(projectName),
        timestamp: new Date().toISOString(),
      },
    ]);
  };

  if (displayMode === 'widget' && !isOpen) {
    return (
      <div className={`chatbot-widget ${isDarkMode ? 'dark' : 'light'}`}>
        <button
          className="chatbot-toggle-btn"
          onClick={() => setIsOpen(true)}
          aria-label={t('chatbot.open')}
        >
          <img src={asviAssistantIcon} alt="" className="chatbot-toggle-img" />
        </button>
      </div>
    );
  }

  const inputDisabled = isLoading || !hasProject;

  return (
    <div className={`chatbot-widget-wrapper ${displayMode === 'widget' ? 'chatbot-widget' : 'chatbot-embedded'} ${isMaximized ? 'maximized' : ''} ${isDarkMode ? 'dark' : 'light'}`}>
      <div className="chatbot-container">
        <div className="chatbot-header">
          <div className="chatbot-title">
            <img src={asviAssistantIcon} alt="" className="chatbot-header-icon" />
            <div className="chatbot-title-text">
              <span>{title || t('chatbot.name')}</span>
              {hasProject && projectName && (
                <span className="chatbot-project-label" title={String(projectKey)}>
                  {projectName}
                </span>
              )}
            </div>
            <span
              className={`chatbot-status-dot ${hasProject ? '' : 'offline'}`}
              title={hasProject ? 'Project scoped' : 'No project selected'}
            />
          </div>
          <div className="chatbot-header-actions">
            <button
              className="chat-action-btn"
              onClick={handleClearChat}
              title="Clear Conversation"
              disabled={!hasProject}
            >
              <Trash2 size={16} />
            </button>
            {displayMode === 'widget' && (
              <>
                <button
                  className="chat-action-btn"
                  onClick={() => setIsMaximized(!isMaximized)}
                  title={isMaximized ? 'Restore' : 'Maximize'}
                  aria-label={isMaximized ? 'Restore' : 'Maximize'}
                >
                  {isMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button
                  className="chat-action-btn"
                  onClick={() => setIsOpen(false)}
                  title="Close"
                >
                  <X size={16} />
                </button>
              </>
            )}
          </div>
        </div>

        {!hasProject && (
          <div className="chatbot-project-banner" role="status">
            {t('chatbot.noProjectBanner')}
          </div>
        )}

        <div className="chatbot-messages">
          {messages.map((msg) => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          {isLoading && (
            <div className="chat-message bot-message">
              <div className="message-avatar">
                <img src={asviAssistantIcon} alt="" className="message-avatar-img" />
              </div>
              <div className="message-bubble typing-indicator">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={inputDisabled}
          placeholder={
            hasProject
              ? 'Type a message...'
              : 'Select a project to chat...'
          }
        />
      </div>
    </div>
  );
};

export default Chatbot;
